// AnimatedCounter.tsx
"use client";
import { useEffect, useRef, useMemo } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import type { FC } from "react";

interface AnimatedCounterProps {
  value: number;
  suffix?: string;
  duration?: number;
  delay?: number;
  decimals?: number;
  className?: string;
}

export const AnimatedCounter: FC<AnimatedCounterProps> = ({
  value,
  suffix = "",
  duration = 2,
  delay = 0,
  decimals = 0,
  className = "text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent",
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const count = useMotionValue(0);

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat("it-IT", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
    [decimals]
  );

  const formatted = useTransform(
    count,
    (v) => `${formatter.format(v)}${suffix}`
  );

  useEffect(() => {
    if (!isInView || value === 0) return;

    const timeout = setTimeout(() => {
      animate(count, value, {
        duration,
        ease: "easeOut",
      });
    }, delay * 1000);

    return () => clearTimeout(timeout);
  }, [isInView, count, value, duration, delay]);

  return (
    <motion.span ref={ref} className={className} aria-live="polite">
      {formatted}
    </motion.span>
  );
};
